import { adt, option } from '@code-expert/prelude';
import type { TaskId } from '/imports/domain';
import type { QuestionFeedback } from '../clozetext/domain/QuestionFeedback';
import type { PublicClozeQuestion } from './PublicQuestion';
import type { QuestionId } from './Question';
import type { Quiz, QuizId } from './Quiz';
import type { QuizSettings } from './QuizSettings';

export type QuizBlankValueMap = Record<QuestionId, Array<string | undefined>>;

interface BasePublicClozeQuiz {
  _id: QuizId;
  taskId: TaskId;
  description: string;
  blankValues: QuizBlankValueMap;
  settings: QuizSettings;
}

export interface PublicClozeQuizEditable extends BasePublicClozeQuiz {
  _tag: 'editable';
  questions: Array<PublicClozeQuestion>;
}

export const makePublicClozeQuizEditable = (
  { _id, taskId, description, settings }: Quiz,
  blankValues: QuizBlankValueMap,
  questions: Array<PublicClozeQuestion>,
): PublicClozeQuizEditable => ({
  _tag: 'editable',
  _id,
  taskId,
  description,
  settings,
  blankValues,
  questions,
});

export interface PublicClozeQuizSubmitted extends BasePublicClozeQuiz {
  _tag: 'submitted';
  questions: Array<PublicClozeQuestion>;
  submissionDate: Date;
  solutionDate: option.Option<Date>;
}

export const makePublicClozeQuizSubmitted = (
  { _id, taskId, description, settings }: Quiz,
  blankValues: QuizBlankValueMap,
  questions: Array<PublicClozeQuestion>,
  submissionDate: Date,
  solutionDate?: Date,
): PublicClozeQuizSubmitted => ({
  _tag: 'submitted',
  _id,
  taskId,
  description,
  settings,
  blankValues,
  questions,
  submissionDate,
  solutionDate: option.fromNullable(solutionDate),
});

export interface PublicClozeQuizFeedback extends BasePublicClozeQuiz {
  _tag: 'feedback';
  questions: Array<PublicClozeQuestion>;
  submissionDate: Date;
  feedback: Record<QuestionId, QuestionFeedback>;
}

export const makePublicClozeQuizFeedback = (
  { _id, taskId, description, settings }: Quiz,
  blankValues: QuizBlankValueMap,
  questions: Array<PublicClozeQuestion>,
  submissionDate: Date,
  feedback: Record<QuestionId, QuestionFeedback>,
): PublicClozeQuizFeedback => ({
  _tag: 'feedback',
  _id,
  taskId,
  description,
  settings,
  blankValues,
  questions,
  submissionDate,
  feedback,
});

export type PublicClozeQuiz =
  PublicClozeQuizEditable | PublicClozeQuizSubmitted | PublicClozeQuizFeedback;

export const foldPublicQuizByState = adt.foldFromProp('_tag');

export type PublicQuiz = PublicClozeQuiz;
